import { FC, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { ReactComponent as ListEmptyStateIcon } from '../../../../../assets/list-empty-state-icon.svg';
import GenericButton, { GenericButtonTypeEnum } from '../../../../../components/GenericButton';
import { ReactComponent as AddIcon } from '../../../../../assets/add-icon.svg';
import { ReactComponent as ArrowRightIcon } from '../../../../../assets/arrow-right-icon.svg';
import { GroupDto, LocationDto } from '../../../../../common/interfaces/kindergarten.interface';
import { useNavigate } from 'react-router-dom';
import { getLocationsWIthGroups } from '../../../../../api/kindergarten.api';
import { centeredContainerStyle, locationCardStyle } from '../../LocationsAndGroupsTab/LocationsAndGroupsTab.styles';
import { GeneralStatusEnum } from '../../../../../common/enums/common.enum';
import { DividerLineBox, GroupRowStack } from './EventConfigurationTab.styles';
import { Trans, useTranslation } from 'react-i18next';

import { palette } from '../../../../../theme';
const EventConfigurationTab: FC<{ kindergartenId: string; kindergartenName: string }> = ({ kindergartenId, kindergartenName }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<LocationDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, [kindergartenId]);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await getLocationsWIthGroups(kindergartenId, GeneralStatusEnum.ACTIVE);
      setLocations(response.results ?? []);
    } catch (error) {
      console.error('Failed to fetch locations and groups', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    navigate(`/${kindergartenId}/event/create/${encodeURIComponent(kindergartenName)}`);
  };

  const handleCreateLocationsAndGroups = () => {
    navigate(`/${kindergartenId}/locations/create/${encodeURIComponent(kindergartenName)}`);
  };

  const handleSingleCreateClick = (groupId: string) => {
    navigate(`/${kindergartenId}/event/create/${encodeURIComponent(kindergartenName)}`, {
      state: { preSelectedGroupId: groupId }
    });
  };

  const handleNavigateToGroupDetails = (groupId: string, locationName: string) => {
    navigate(
      `/${kindergartenId}/groups/${groupId}/event-configuration/${encodeURIComponent(kindergartenName)}/${encodeURIComponent(locationName)}`
    );
  };

  const hasGroupWithoutEvents = useMemo(() => {
    return locations?.some(location => location.groups?.some(group => group.hasEvents === false));
  }, [locations]);

  const hasAnyLocationsOrGroups = useMemo(() => {
    return locations.length > 0 && locations.some(location => (location.groups?.length ?? 0) > 0);
  }, [locations]);

  const handleAddEventCreateClick = (group: GroupDto, locationName?: string) => {
    if (!group.id) return;

    if (group.hasEvents && locationName) {
      handleNavigateToGroupDetails(group.id, locationName);
    } else {
      handleSingleCreateClick(group.id);
    }
  };

  if (loading) {
    return (
      <Box sx={centeredContainerStyle}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && !hasAnyLocationsOrGroups) {
    return (
      <Box sx={centeredContainerStyle}>
        <ListEmptyStateIcon style={{ fontSize: 40, color: 'text.secondary' }} />
        <Typography sx={{ fontWeight: 'bold' }}>{t('events.cannotCreateYet')}</Typography>
        <Typography sx={{ mt: 1, text: 'primary.main', textAlign: 'center' }}>
          <Trans i18nKey="events.createGroupFirst" />
        </Typography>
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateLocationsAndGroups}
          label={t('events.createLocationsAndGroups')}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 24
          }}
        >
          {t('kindergarten.tabs.eventConfiguration')}
        </Typography>
        {hasGroupWithoutEvents && (
          <GenericButton
            buttonType={GenericButtonTypeEnum.Primary}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
            label={t('common.create')}
          />
        )}
      </Stack>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {locations.map(location => (
          <Paper key={location.id} sx={locationCardStyle}>
            <Stack sx={{ px: 1.5, py: 2, backgroundColor: palette.surface.subtle, borderRadius: '10px' }}>
              <Typography
                fontWeight="bold"
                sx={theme => ({
                  color: theme.palette.primary.main,
                  fontSize: 14
                })}
              >
                {location.name}
              </Typography>
            </Stack>

            <DividerLineBox />
            <Box sx={{ flexGrow: 1, maxHeight: 56 * 4, overflowY: 'auto' }}>
              {location.groups &&
                location.groups.length > 0 &&
                location.groups.map((group, index) => (
                  <GroupRowStack
                    key={group.id}
                    isLast={index === (location.groups?.length ?? 0) - 1}
                    onClick={() => handleAddEventCreateClick(group, location.name)}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'primary.main',
                          fontSize: 14
                        }}
                      >
                        {group.name}
                      </Typography>
                    </Stack>
                    {group.hasEvents ? <ArrowRightIcon style={{ cursor: 'pointer' }} /> : <AddIcon style={{ cursor: 'pointer' }} />}
                  </GroupRowStack>
                ))}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default EventConfigurationTab;
